// ────────────────────────────────────────────────────────────────────────────
// Slide Editor – Markdown Utilities
// ────────────────────────────────────────────────────────────────────────────
//
// Shared helpers for converting text content (which may contain Markdown)
// into HTML for rendering in the canvas (TextRenderer) and for HTML export
// (exportHtml).
//
// Variable values injected via {{variable}} placeholders often contain
// Markdown formatting (bullet lists, bold, etc.). These utilities detect
// Markdown syntax and convert it to HTML using the `marked` library.
// ────────────────────────────────────────────────────────────────────────────

import { marked } from 'marked'

// ─── Marked options (passed inline, never mutate global state) ──────────────

const MARKED_OPTIONS = {
  breaks: true, // Convert single line breaks to <br>
  gfm: true, // GitHub Flavored Markdown (tables, strikethrough, etc.)
  async: false as const,
}

// ─── Markdown detection ─────────────────────────────────────────────────────

/**
 * Heuristic check for whether a string contains Markdown formatting.
 *
 * We look for common Markdown patterns:
 *  - Headings:          `# `, `## `, etc.
 *  - Bullet lists:      `- `, `* `, `+ ` at start of line (must be preceded by BOL)
 *  - Ordered lists:     `1. `, `2. ` etc. at start of line
 *  - Bold:              `**text**` or `__text__`
 *  - Links:             `[text](url)`
 *  - Images:            `![alt](url)`
 *  - Inline code:       `` `code` ``
 *  - Blockquotes:       `> ` at start of line
 *  - Horizontal rules:  `---`, `***`, `___` on their own line
 *  - Strikethrough:     `~~text~~`
 *
 * NOTE: We intentionally avoid lookbehind assertions for maximum browser
 * compatibility, and we skip single-asterisk/underscore italic detection
 * to avoid false positives on normal prose.
 */
const MARKDOWN_PATTERNS: RegExp[] = [
  /^#{1,6}\s/m, // headings
  /^[\t ]*[-*+] \S/m, // unordered list items (dash/star/plus, space, then non-space)
  /^[\t ]*\d+\.\s/m, // ordered list items
  /\*\*[^*]+\*\*/, // bold with **
  /__[^_]+__/, // bold with __
  /\[([^\]]+)\]\([^)]+\)/, // links
  /!\[([^\]]*)\]\([^)]+\)/, // images
  /`[^`]+`/, // inline code
  /^>\s/m, // blockquotes
  /^[-*_]{3,}\s*$/m, // horizontal rules
  /~~[^~]+~~/, // strikethrough
]

/**
 * Returns true if the string appears to contain Markdown formatting.
 */
export function containsMarkdown(text: string): boolean {
  if (!text) return false
  return MARKDOWN_PATTERNS.some((pattern) => pattern.test(text))
}

// ─── Content → HTML conversion ──────────────────────────────────────────────

/**
 * Convert text content to HTML, handling three cases:
 *
 *  1. Already HTML (starts with `<`): pass through as-is
 *     (backwards compat with old Tiptap-era content).
 *  2. Contains Markdown syntax: parse with `marked`.
 *  3. Plain text: escape HTML entities and convert newlines to `<br>`.
 *
 * This is the single source of truth for turning element `content` into
 * renderable HTML — used by both `TextRenderer` (canvas) and `exportHtml`.
 */
export function contentToHtml(content: string): string {
  if (!content) return ''

  const trimmed = content.trim()

  // Case 1: Legacy HTML content (from Tiptap era) — pass through
  if (trimmed.startsWith('<')) return content

  // Case 2: Markdown content or content with newlines — parse with marked.
  // We always go through marked when the text contains any newline so that
  // blank lines (intentional line-breaks added by the user) are preserved as
  // paragraph breaks in the rendered HTML.
  if (containsMarkdown(trimmed) || content.includes('\n')) {
    try {
      // marked.parse returns string synchronously when async: false
      const html = marked.parse(content, MARKED_OPTIONS) as string
      // Trim trailing newline that marked often adds
      return html.replace(/\n$/, '')
    } catch {
      // If marked fails for any reason, fall through to plain-text path
    }
  }

  // Case 3: Plain text (single line, no markdown) — escape HTML entities
  return escapeHtml(content)
}

// ─── HTML escaping ──────────────────────────────────────────────────────────

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}
